// drawOla cloud sync: the panel behind the exclamation icon. Fork-only.
//
// Rendered once at the app root (a sibling of the Studio and the editor), so
// both the Studio card and the breadcrumb open the same panel. It reuses the
// Studio dialog classes and the Studio portal wrapper for theme tokens, and
// translates through `useStudioT` because it lives outside `<Excalidraw>`.

import { THEME } from "@excalidraw/common";
import clsx from "clsx";
import React, { useCallback, useEffect, useRef, useState } from "react";

import type { Theme } from "@excalidraw/element/types";

import { getDocMeta } from "../../storage/multiFileStore";
import { formatMB } from "../../storage/quota";
import { useStudioT } from "../../studio/i18n";

import type { DocMeta } from "../../storage/types";
import type { CloudSyncApi } from "./useCloudSync";

type Props = {
  cloudSync: CloudSyncApi;
  theme: Theme;
};

/** Shows the reason sync stopped for one draw and the action that fixes it. */
export const SyncRepairPanel: React.FC<Props> = ({ cloudSync, theme }) => {
  const t = useStudioT();
  const { engine, repairDocId, closeRepair } = cloudSync;
  const [meta, setMeta] = useState<DocMeta | null>(null);
  const [busy, setBusy] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);

  // re-read on every engine change, so a fixed draw closes the panel
  useEffect(() => {
    if (!repairDocId) {
      setMeta(null);
      return undefined;
    }
    let cancelled = false;
    getDocMeta(repairDocId)
      .then((next) => {
        if (!cancelled) {
          setMeta(next);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMeta(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [repairDocId, cloudSync.version]);

  const status = meta ? cloudSync.statusOf(meta) : null;

  useEffect(() => {
    if (repairDocId && meta && status && status.kind !== "error") {
      closeRepair();
    }
  }, [repairDocId, meta, status, closeRepair]);

  const isOpen = !!repairDocId && !!meta?.cloud && status?.kind === "error";

  // the icon that opens the panel sits outside it, so focus has to be moved in;
  // without this the Escape handler below never sees a key
  useEffect(() => {
    if (isOpen) {
      cancelRef.current?.focus();
    }
  }, [repairDocId, isOpen]);

  /** Run one repair action with the buttons disabled while it is in flight. */
  const run = useCallback(async (action: () => Promise<void>) => {
    setBusy(true);
    try {
      await action();
    } finally {
      setBusy(false);
    }
  }, []);

  // repeats the `isOpen` conditions so TypeScript narrows `meta` and `status`
  if (!isOpen || !meta?.cloud || status?.kind !== "error") {
    return null;
  }

  const provider = cloudSync.providerName(meta);
  const providerId = meta.cloud.providerId;
  const docId = meta.id;
  const limit = formatMB(engine.getProvider(providerId)?.maxUploadBytes ?? 0);

  let body = "";
  let actions: React.ReactNode = null;

  /** One action button of the panel; `primary` marks the suggested choice. */
  const button = (
    label: string,
    action: () => Promise<void>,
    primary = false,
  ) => (
    <button
      type="button"
      className={clsx("drawola-studio-dialog__button", {
        "drawola-studio-dialog__button--primary": primary,
      })}
      disabled={busy}
      onClick={() => void run(action)}
    >
      {label}
    </button>
  );

  switch (status.reason) {
    case "auth":
      body = t("cloud.sync.authBody", { provider });
      actions = button(
        t("cloud.sync.authAction", { provider }),
        () => engine.signIn(providerId),
        true,
      );
      break;
    case "conflict":
      body = t("cloud.sync.conflictBody", { provider });
      actions = (
        <>
          {button(t("cloud.overwrite"), () =>
            engine.resolveConflict(docId, "overwrite"),
          )}
          {button(
            t("cloud.loadRemote"),
            () => engine.resolveConflict(docId, "loadRemote"),
            true,
          )}
        </>
      );
      break;
    case "fileMissing":
      body = t("cloud.sync.fileMissingBody", { provider });
      actions = (
        <>
          {button(t("cloud.sync.unlink"), () => engine.unlink(docId))}
          {button(t("cloud.sync.reupload"), () => engine.reupload(docId), true)}
        </>
      );
      break;
    case "tooLarge":
      body = t("cloud.sync.tooLargeBody", { provider, limit });
      actions = button(
        t("cloud.sync.unlink"),
        () => engine.unlink(docId),
        true,
      );
      break;
  }

  return (
    <div
      className={clsx("excalidraw", "drawola-studio-portal", {
        "theme--dark": theme === THEME.DARK,
      })}
    >
      <div
        className="drawola-studio-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={t("cloud.sync.repairTitle")}
        onKeyDown={(event) => {
          // keep editor shortcuts from seeing dialog keystrokes
          event.stopPropagation();
          if (event.key === "Escape") {
            closeRepair();
          }
        }}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            closeRepair();
          }
        }}
      >
        <div className="drawola-studio-dialog__panel">
          <h3 className="drawola-studio-dialog__title">
            {t("cloud.sync.repairTitle")}
          </h3>
          <p className="drawola-studio-dialog__body">{body}</p>
          <div className="drawola-studio-dialog__actions">
            <button
              ref={cancelRef}
              type="button"
              className="drawola-studio-dialog__button"
              onClick={closeRepair}
            >
              {t("cloud.cancel")}
            </button>
            {actions}
          </div>
        </div>
      </div>
    </div>
  );
};
